import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TosNotificationsResponse, TosTroubleUser } from "./types";
import { userLabel } from "./helpers";

export function TosNotificationsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const dataQuery = useQuery<TosNotificationsResponse>({
    queryKey: ["/api/admin/tos-notifications"],
    queryFn: async () => {
      const res = await fetch("/api/admin/tos-notifications", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load ToS notifications");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const runMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/tos-notifications/run");
      return res.json();
    },
    onSuccess: (data: { eligibleCount?: number; sent?: number; failed?: number }) => {
      toast({
        title: "ToS notifier ran",
        description: `${data.sent ?? 0} sent, ${data.failed ?? 0} failed (of ${data.eligibleCount ?? 0} eligible).`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tos-notifications"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Notifier failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const resetMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (userId: number) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/tos-notifications/users/${userId}/reset-attempts`,
      );
      return res.json();
    },
    onSuccess: (_data, userId) => {
      toast({
        title: "Retry counter reset",
        description: `User #${userId} will be retried on the next cron pass.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tos-notifications"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Reset failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const data = dataQuery.data;
  const currentRun = data?.runs.find((r) => r.version === data.currentVersion) ?? null;

  function userLabel(u: TosTroubleUser) {
    return u.fullName?.trim() || u.firstName?.trim() || u.email || `User #${u.id}`;
  }

  function renderUserTable(users: TosTroubleUser[], emptyText: string, kind: "failed" | "capped") {
    if (users.length === 0) {
      return (
        <p
          className="text-sm text-muted-foreground"
          data-testid={`text-tos-${kind}-empty`}
        >
          {emptyText}
        </p>
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="text-right">Attempts</TableHead>
            <TableHead>Last error</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`row-tos-${kind}-${u.id}`}>
              <TableCell className="text-sm">{userLabel(u)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {u.email ?? "—"}
              </TableCell>
              <TableCell
                className="text-right font-semibold"
                data-testid={`text-tos-${kind}-attempts-${u.id}`}
              >
                {u.attempts}
                {data ? ` / ${data.maxAttempts}` : ""}
              </TableCell>
              <TableCell
                className="text-xs text-muted-foreground max-w-md truncate"
                title={u.lastError ?? ""}
                data-testid={`text-tos-${kind}-error-${u.id}`}
              >
                {u.lastError ?? "—"}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={resetMutation.isPending}
                  onClick={() => resetMutation.mutate(u.id)}
                  data-testid={`button-tos-reset-${u.id}`}
                >
                  {resetMutation.isPending && resetMutation.variables === u.id ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Re-trigger
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-tos-title">
            Terms of Service Notifications
          </h3>
          <p className="text-sm text-muted-foreground">
            Live progress of the bulk ToS-update email blast. Rows for the current
            version ({data?.currentVersionLabel ?? "…"}) update every minute.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          data-testid="button-tos-run"
        >
          {runMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Run notifier now
        </Button>
      </div>

      <Card className="p-6" data-testid="card-tos-runs">
        <h4 className="font-semibold mb-3">Per-version runs</h4>
        {dataQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (data?.runs ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-tos-runs-empty">
            No notification runs recorded yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Eligible seen</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead>Summary email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.runs ?? []).map((r) => {
                const isCurrent = r.version === data?.currentVersion;
                return (
                  <TableRow key={r.version} data-testid={`row-tos-run-${r.version}`}>
                    <TableCell className="text-sm font-mono">
                      <div className="flex items-center gap-2">
                        <span>{r.version}</span>
                        {isCurrent && (
                          <Badge
                            variant="outline"
                            className="border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
                            data-testid={`badge-tos-current-${r.version}`}
                          >
                            Current
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(r.lastRunAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">{r.runCount}</TableCell>
                    <TableCell className="text-right">{r.totalEligibleSeen}</TableCell>
                    <TableCell className="text-right text-green-700 font-semibold">
                      {r.totalSent}
                    </TableCell>
                    <TableCell
                      className={`text-right font-semibold ${r.totalFailed > 0 ? "text-red-600" : ""}`}
                    >
                      {r.totalFailed}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.summaryEmailSentAt ? (
                        <span
                          className="inline-flex items-center gap-1 text-green-700"
                          data-testid={`text-tos-summary-sent-${r.version}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {new Date(r.summaryEmailSentAt).toLocaleString()}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-muted-foreground"
                          data-testid={`text-tos-summary-pending-${r.version}`}
                        >
                          <Clock className="h-3.5 w-3.5" />
                          Not sent
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {currentRun && (
          <p
            className="text-xs text-muted-foreground mt-3"
            data-testid="text-tos-current-summary"
          >
            Current version started at{" "}
            {new Date(currentRun.firstStartedAt).toLocaleString()} — {currentRun.totalSent} of{" "}
            {currentRun.totalEligibleSeen} delivered, {currentRun.totalFailed} failed across{" "}
            {currentRun.runCount} run(s).
          </p>
        )}
      </Card>

      <Card className="p-6" data-testid="card-tos-failed">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold">Pending retries</h4>
            <p className="text-xs text-muted-foreground">
              Users whose latest send failed but who still have retries remaining (under{" "}
              {data?.maxAttempts ?? "—"} attempts).
            </p>
          </div>
          <Badge variant="outline" data-testid="badge-tos-failed-count">
            {data?.failed.length ?? 0}
          </Badge>
        </div>
        {dataQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          renderUserTable(
            data?.failed ?? [],
            "No users are pending a retry right now.",
            "failed",
          )
        )}
      </Card>

      <Card className="p-6" data-testid="card-tos-capped">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Retry cap reached
            </h4>
            <p className="text-xs text-muted-foreground">
              Users who hit the {data?.maxAttempts ?? "—"}-attempt cap. The cron job will
              skip them until you re-trigger.
            </p>
          </div>
          <Badge variant="outline" data-testid="badge-tos-capped-count">
            {data?.capped.length ?? 0}
          </Badge>
        </div>
        {dataQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          renderUserTable(
            data?.capped ?? [],
            "No users are currently at the retry cap.",
            "capped",
          )
        )}
      </Card>
    </div>
  );
}
